/**
 * Utilities for cleaning and processing AI stream responses
 * Shared between frontend and backend
 */

/**
 * Extracts and removes a complete JSON object from text
 * @param text - The text containing JSON
 * @param startPattern - Pattern to find JSON start (e.g., '{"type"')
 * @returns Object with cleaned text and extracted JSON data
 */
export function extractAndRemoveJSON(text: string, startPattern = '{"type"') {
  // Look for JSON with different formatting patterns
  const patterns = [
    startPattern, // {"type"
    startPattern.replace('{', '{ '), // { "type"
    startPattern.replace('{', '{\n    '), // {\n    "type"
    startPattern.replace('{', '{\n  '), // {\n  "type"
    startPattern.replace('{', '{\n\t'), // {\n\t"type"
  ]

  let jsonStartIndexToUse = -1
  for (const pattern of patterns) {
    const index = text.indexOf(pattern)
    if (index !== -1) {
      jsonStartIndexToUse = index
      break
    }
  }

  let extractedJson = null
  let cleanedText = text

  if (jsonStartIndexToUse !== -1) {
    // Try to find the complete JSON by attempting to parse incrementally
    let jsonEndIndex = -1

    // Start from a reasonable minimum length and increment
    for (let i = jsonStartIndexToUse + 10; i <= text.length; i++) {
      const potentialJson = text.substring(jsonStartIndexToUse, i)
      try {
        // Try to parse the substring as JSON
        extractedJson = JSON.parse(potentialJson)
        // If successful, this is valid JSON, update the end index
        jsonEndIndex = i
      } catch {
        // Not valid JSON yet, continue
      }
    }

    // If we found valid JSON, remove it from text
    if (jsonEndIndex > 0) {
      cleanedText =
        text.substring(0, jsonStartIndexToUse).trim() +
        (text.substring(jsonEndIndex).trim()
          ? `\n${text.substring(jsonEndIndex).trim()}`
          : '')
    }
  }

  return { cleanedText, extractedJson }
}

/**
 * Cleans text by removing system tags and JSON objects
 * @param text - The text to clean
 * @param removeJson - Whether to remove JSON objects (default: true)
 * @returns Object with cleaned text and extracted JSON (if removeJson is true)
 */
export function cleanStreamText(text: string, removeJson = true) {
  // Remove system tags and extract Output content
  let cleaned = text
    .replace(/<System>[\s\S]*?<\/System>/g, '')
    .replace(/<UserMessage>[\s\S]*?<\/UserMessage>/g, '')
    .replace(/<Output>([\s\S]*?)<\/Output>/g, '$1')

  let extractedJson = null

  // Remove JSON if requested
  if (removeJson) {
    // First try to remove the standard JSON with "type" field
    const result = extractAndRemoveJSON(cleaned)
    cleaned = result.cleanedText
    extractedJson = result.extractedJson

    // Also remove asset JSON objects that start with {"source" or arrays of such objects
    // This handles AI-generated asset data that shouldn't be shown as text
    cleaned = cleaned
      .replace(/\[?\s*\{\s*"source"\s*:\s*"[^"]*"[\s\S]*?\}\s*\]?/g, '')
      .trim()
  }

  // Final whitespace cleanup
  cleaned = cleaned
    .replace(/^\s+|\s+$/g, '') // Trim start and end
    .replace(/\n{3,}/g, '\n\n') // Replace 3+ newlines with 2

  return { cleanedText: cleaned, extractedJson }
}
